/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Typography, Icon, Button, Stack, EmptyState, Modal } from '@mdsol/onex-design'
// @ts-expect-error - createColumnHelper is exported but not in TypeScript definitions
import { DataGrid, createColumnHelper } from '@mdsol/onex-design/DataGrid'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import { getFileReferences, deleteDocumentRefs, getFilePath, type PathItem } from '../../../services/api/files'
import { useEsignature } from '../../../hooks/useEsignature'
import { useModal } from '../../../contexts/ModalContext'
import AddReferencesModal from './addReferences/AddReferencesModal'
import { toast } from '../../../utils/toast'
import { USER_ROLE } from '../../../constants/user'
import { PAGE_SIZES_ARRAY, DEFAULT_PAGE_SIZE } from '../../../constants/pagination'
import styles from './DocumentReferences.module.css'
import { MedidataLogo } from '../../../components/MedidataLogo'
import { buildUrlWithCSelections } from '~/utils/navigation'

const MAX_REFERENCES = 100

/**
 * Returns the set of keys that are true in a selection object.
 * Used for O(N) equality checks instead of deep object comparison.
 */
const getActiveKeys = (selectionObj: Record<string, boolean> | undefined): Set<string> => {
  const active = new Set<string>()
  if (!selectionObj) return active
  for (const key in selectionObj) {
    if (selectionObj[key]) active.add(key)
  }
  return active
}

/** O(N) equality check between two Sets */
const areSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false
  let equal = true
  a.forEach((item) => {
    if (!b.has(item)) equal = false
  })
  return equal
}

// Roles that cannot add/modify references
const READ_ONLY_ROLES = [
  USER_ROLE.AUDITOR,
  USER_ROLE.INSPECTOR,
  USER_ROLE.ETMF_STUDY_USER,
  USER_ROLE.ETMF_SITE_USER,
  USER_ROLE.ETMF_SPONSOR
]

interface EnrichedReference {
  referenceFsiId: number
  referenceType: string
  filename: string
  artifact: string
  product: string
  study: string
  environment: string
  country: string
  site: string
}

// [Change 1] Replaced LocationState with component props for modal-based usage
interface DocumentReferencesModalProps {
  fsiId: number
  fsiName: string
  canManageReferences: boolean
  onClose?: () => void
}

/**
 * Parse ETMF hierarchy from file path.
 * Path structure: Root / [Clinical|Medidata] / Product / Study / Environment / Country / Site / Zone / Section / ...
 * The "Clinical" or "Medidata" folder at index 1 must be skipped to reach the actual hierarchy levels.
 */
function parseEtmfHierarchy(pathItems: PathItem[]): {
  product: string
  study: string
  environment: string
  country: string
  site: string
} {
  const names = pathItems.map((p) => p.name)
  // Skip the "Clinical" or "Medidata" folder at index 1 if present
  const secondSegment = names[1]?.toLowerCase()
  const offset = secondSegment === 'clinical' || secondSegment === 'medidata' ? 2 : 1
  return {
    product: names[offset] || '-',
    study: names[offset + 1] || '-',
    environment: names[offset + 2] || '-',
    country: names[offset + 3] || '-',
    site: names[offset + 4] || '-'
  }
}

const columnHelper = createColumnHelper()

const DocumentReferencesModal: React.FC<DocumentReferencesModalProps> = ({
  fsiId,
  fsiName,
  canManageReferences,
  onClose
}) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { showModal } = useModal()
  const { signAction } = useEsignature()

  const [isOpen, setIsOpen] = useState(true)

  // RBAC: Check if the current user role is read-only
  const currentRole = context.service.auth.getRole()
  const isReadOnly = !canManageReferences || READ_ONLY_ROLES.includes(currentRole as string)

  // References state
  const [references, setReferences] = useState<EnrichedReference[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([])
  const [globalSearchValue, setGlobalSearchValue] = useState('')
  // Cross-page selection: stores selections per page index
  const [rowSelectionByPages, setRowSelectionByPages] = useState<Record<number, Record<string, boolean>>>({})
  // Forces a new allRowSelectionObj reference when DataGrid internally clears selections on page/sort change
  const [resyncTrigger, setResyncTrigger] = useState(0)
  const [selectAllError, setSelectAllError] = useState<string | null>(null)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const [isBulkDeletePending, setIsBulkDeletePending] = useState(false)

  // Flattened selection across all pages — passed to DataGrid's state.rowSelection
  const allRowSelectionObj = useMemo(() => {
    const merged: Record<string, boolean> = {}
    Object.values(rowSelectionByPages).forEach((pageSelection) => {
      Object.entries(pageSelection).forEach(([key, value]) => {
        if (value) merged[key] = true
      })
    })
    return merged
  }, [rowSelectionByPages, resyncTrigger])

  // Ref to always have the latest merged selection without adding it as a dependency
  const allRowSelectionRef = useRef(allRowSelectionObj)
  allRowSelectionRef.current = allRowSelectionObj

  // Flag to distinguish DataGrid's forced setRowSelection({}) on pagination/sort change
  // from genuine user uncheck
  const dgInternalClearRef = useRef(false)

  // Caches the full EnrichedReference details for every selected row across all pages.
  // The delete API needs filename + referenceType, not just the IDs, so we cannot
  // reconstruct the payload from `references` alone (which holds the current page only).
  const selectedItemsMapRef = useRef<Map<number, EnrichedReference>>(new Map())

  // True when the user has opted into "select all across all pages" mode.
  // When set, selectedDisplayCount shows totalElements and the delete path fetches
  // all references to build a complete payload.
  const [isAllDataSelected, setIsAllDataSelected] = useState(false)
  // Debounce ref prevents spurious rapid-fire false signals from DataGrid's onAllDataSelected
  const allDataSelectedAtRef = useRef<number>(0)

  // [Fix #1] eTMF context for the base document — pre-fills AddReferencesModal filters
  const [documentEtmfContext, setDocumentEtmfContext] = useState<
    | {
        product?: string
        study?: string
        environment?: string
        country?: string
        site?: string
      }
    | undefined
  >(undefined)

  // Load references (API now returns enriched fields directly)
  const loadReferences = useCallback(async () => {
    if (!fsiId) return

    setIsLoading(true)
    try {
      const sortParam = sorting.length > 0 ? sorting[0] : undefined
      const response = await getFileReferences(fsiId, {
        ...(sortParam ? { sortBy: sortParam.id, sortDesc: sortParam.desc } : {}),
        pageStart: pagination.pageIndex,
        pageSize: pagination.pageSize,
        searchText: globalSearchValue ?? ''
      })
      setTotalElements(response.totalElements)
      setReferences(
        response.elements.map((ref) => ({
          referenceFsiId: ref.referenceFsiId,
          referenceType: ref.referenceType,
          filename: ref.filename,
          artifact: ref.artifact || '-',
          product: ref.product || '-',
          study: ref.study || '-',
          environment: ref.environment || '-',
          country: ref.country || '-',
          site: ref.site || '-'
        }))
      )
    } catch (error) {
      console.error('Failed to load references:', error)
      toast.error(t('ERROR'))
    } finally {
      setIsLoading(false)
    }
  }, [fsiId, sorting, pagination.pageIndex, pagination.pageSize, t, globalSearchValue])

  // [Fix #1] Load eTMF hierarchy for the base document to pre-fill AddReferencesModal context.
  // Only pre-populate context filters when the document resides under a "clinical" path.
  useEffect(() => {
    if (!fsiId) return
    getFilePath(fsiId)
      .then((pathItems) => {
        // Check if any segment in the path contains "clinical" (case-insensitive)
        const isClinicalPath = pathItems.some((item) => item.name.toLowerCase().includes('clinical'))

        if (!isClinicalPath) {
          // Not a clinical/eTMF document — leave context undefined (filters stay default)
          return
        }

        const hierarchy = parseEtmfHierarchy(pathItems)
        // Only set context if hierarchy values are meaningful (not '-')
        const ctx: { product?: string; study?: string; environment?: string; country?: string; site?: string } = {}
        if (hierarchy.product && hierarchy.product !== '-') ctx.product = hierarchy.product
        if (hierarchy.study && hierarchy.study !== '-') ctx.study = hierarchy.study
        if (hierarchy.environment && hierarchy.environment !== '-') ctx.environment = hierarchy.environment
        if (hierarchy.country && hierarchy.country !== '-') ctx.country = hierarchy.country
        if (hierarchy.site && hierarchy.site !== '-') ctx.site = hierarchy.site
        if (Object.keys(ctx).length > 0) setDocumentEtmfContext(ctx)
      })
      .catch(() => {
        // Not an eTMF document — leave context undefined
      })
  }, [fsiId])

  useEffect(() => {
    loadReferences()
  }, [loadReferences])

  // Filtered references (global search)
  const filteredReferences = useMemo(() => {
    if (!globalSearchValue) return references
    const search = globalSearchValue.toLowerCase()
    return references.filter(
      (ref) =>
        ref.filename.toLowerCase().includes(search) ||
        ref.artifact.toLowerCase().includes(search) ||
        ref.product.toLowerCase().includes(search) ||
        ref.study.toLowerCase().includes(search) ||
        ref.environment.toLowerCase().includes(search) ||
        ref.country.toLowerCase().includes(search) ||
        ref.site.toLowerCase().includes(search)
    )
  }, [references, globalSearchValue])

  const existingLinkedIds = useMemo(
    () => Array.from(new Set(references.map((ref) => ref.referenceFsiId))),
    [references]
  )

  // Handle remove reference — shows confirmation dialog before deleting
  const handleRemoveReference = useCallback((referenceFsiId: number) => {
    setPendingDeleteId(referenceFsiId)
    setIsBulkDeletePending(false)
    setShowConfirmationDialog(true)
  }, [])

  // Handle adding references via the new AddReferencesModal
  const handleAddReferences = useCallback(() => {
    showModal(
      <AddReferencesModal
        fsiId={fsiId}
        etmfContext={documentEtmfContext}
        canManageReferences={canManageReferences}
        requireESign={context.service.auth.requireESignatureForFileCommands()}
        existingLinkedIds={existingLinkedIds}
        onConfirm={() => {
          // Refresh the references grid after successful add
          loadReferences()
        }}
        onClose={() => {}}
      />,
      { keepParentModalsOpen: true }
    )
  }, [
    fsiId,
    showModal,
    canManageReferences,
    documentEtmfContext,
    context.service.auth,
    loadReferences,
    existingLinkedIds
  ])

  // Derived count of currently selected references (across all pages)
  const selectedCount = useMemo(() => Object.values(allRowSelectionObj).filter(Boolean).length, [allRowSelectionObj])

  // When isAllDataSelected the displayed count is the server total, not just visited pages
  const selectedDisplayCount = isAllDataSelected ? totalElements : selectedCount

  useEffect(() => {
    if (selectedCount > MAX_REFERENCES) {
      setSelectAllError(
        `The number of documents selected (${selectedCount}) exceeds the maximum number of references allowed`
      )
    } else {
      setSelectAllError(null)
    }
  }, [selectedCount])

  // Handle bulk remove of selected references — shows confirmation dialog before deleting
  const handleBulkRemoveReferences = useCallback(() => {
    if (selectedDisplayCount === 0) return
    setIsBulkDeletePending(true)
    setPendingDeleteId(null)
    setShowConfirmationDialog(true)
  }, [selectedDisplayCount])

  // Perform the actual delete after confirmation
  const handleLeavePage = useCallback(async () => {
    setShowConfirmationDialog(false)

    if (isBulkDeletePending) {
      if (!fsiId) return

      let payload: Array<{ filename: string; referenceFsiId: number; referenceType: string; obsolete: false }> = []

      if (isAllDataSelected) {
        // Fetch every reference page so the payload is complete regardless of which
        // pages the user visited. totalElements is used as the page size.
        try {
          const allRefsResponse = await getFileReferences(fsiId, {
            pageStart: 0,
            pageSize: totalElements || 1000,
            searchText: ''
          })
          payload = allRefsResponse.elements.map((ref) => ({
            filename: ref.filename,
            referenceFsiId: ref.referenceFsiId,
            referenceType: ref.referenceType,
            obsolete: false as const
          }))
        } catch {
          toast.error(t('ERROR'))
          setIsBulkDeletePending(false)
          return
        }
      } else {
        const selectedIds = Object.keys(allRowSelectionObj)
          .filter((key) => allRowSelectionObj[key])
          .map(Number)
        if (selectedIds.length === 0) return

        // Build payload from the cross-page cache — `references` only holds the current page
        payload = selectedIds
          .map((id) => {
            const ref = selectedItemsMapRef.current.get(id)
            return ref
              ? {
                  filename: ref.filename,
                  referenceFsiId: ref.referenceFsiId,
                  referenceType: ref.referenceType,
                  obsolete: false as const
                }
              : null
          })
          .filter((item): item is NonNullable<typeof item> => item !== null)
      }

      if (payload.length === 0) return
      const deletedIds = payload.map((p) => p.referenceFsiId)

      try {
        if (context.service.auth.requireESignatureForFileCommands()) {
          const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_UPDATE_REFERENCES')
          await deleteDocumentRefs(fsiId, payload, validation)
        } else {
          await deleteDocumentRefs(fsiId, payload)
        }
        setRowSelectionByPages({})
        selectedItemsMapRef.current.clear()
        setIsAllDataSelected(false)
        toast.success(
          t('REFERENCES_REMOVED_SUCCESS', { defaultValue: 'The selected reference(s) were removed' }),
          'bottom-right'
        )
        loadReferences()
      } catch (error) {
        console.error('Failed to remove references:', error)
        toast.error(t('ERROR'))
      } finally {
        setIsBulkDeletePending(false)
      }
    } else if (pendingDeleteId !== null) {
      if (!fsiId) return
      const refToRemove = references.find((ref) => ref.referenceFsiId === pendingDeleteId)
      if (!refToRemove) return

      const payload = [
        {
          filename: refToRemove.filename,
          referenceFsiId: refToRemove.referenceFsiId,
          referenceType: refToRemove.referenceType,
          obsolete: false
        }
      ]

      try {
        if (context.service.auth.requireESignatureForFileCommands()) {
          const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_UPDATE_REFERENCES')
          await deleteDocumentRefs(fsiId, payload, validation)
        } else {
          await deleteDocumentRefs(fsiId, payload)
        }
        setRowSelectionByPages({})
        toast.success(
          t('REFERENCES_REMOVED_SUCCESS', { defaultValue: 'The selected reference(s) were removed' }),
          'bottom-right'
        )
        loadReferences()
      } catch (error) {
        console.error('Failed to remove reference:', error)
        toast.error(t('ERROR'))
      } finally {
        setPendingDeleteId(null)
      }
    }
  }, [
    fsiId,
    isBulkDeletePending,
    pendingDeleteId,
    references,
    allRowSelectionObj,
    isAllDataSelected,
    totalElements,
    context.service.auth,
    signAction,
    t,
    loadReferences
  ])

  const handleCancel = useCallback(() => {
    setShowConfirmationDialog(false)
    setPendingDeleteId(null)
    setIsBulkDeletePending(false)
  }, [])

  // The DataGrid passes a plain selection object (not an updater).
  // Validate MAX_REFERENCES in a separate derived effect to avoid
  // impure side effects inside a state updater.
  const handleRowSelectionChange = useCallback(
    (newSelection: Record<string, boolean>) => {
      const currentAll = allRowSelectionRef.current

      const newActiveKeys = getActiveKeys(newSelection)
      const curActiveKeys = getActiveKeys(currentAll)

      // Identical → spurious sync or re-render echo → skip
      if (areSetsEqual(newActiveKeys, curActiveKeys)) return

      // DataGrid is clearing due to internal pagination/sort change → skip + resync
      if (dgInternalClearRef.current) {
        dgInternalClearRef.current = false
        setResyncTrigger((prev) => prev + 1)
        return
      }

      // Built-in "Clear Selection" button: empty incoming + we have selections
      if (newActiveKeys.size === 0 && curActiveKeys.size > 0) {
        setRowSelectionByPages({})
        selectedItemsMapRef.current.clear()
        setIsAllDataSelected(false)
        return
      }

      // Genuine user interaction — extract current page's portion from displayed rows
      const newPagePortion: Record<string, boolean> = {}
      filteredReferences.forEach((ref) => {
        const id = String(ref.referenceFsiId)
        if (newActiveKeys.has(id)) {
          newPagePortion[id] = true
        }
      })

      // Sync selectedItemsMap: add newly selected rows, remove deselected rows
      filteredReferences.forEach((ref) => {
        if (newPagePortion[String(ref.referenceFsiId)]) {
          selectedItemsMapRef.current.set(ref.referenceFsiId, ref)
        } else {
          selectedItemsMapRef.current.delete(ref.referenceFsiId)
        }
      })

      setRowSelectionByPages((prev) => {
        const existingPageKeys = getActiveKeys(prev[pagination.pageIndex])
        const newPageKeys = getActiveKeys(newPagePortion)

        if (areSetsEqual(existingPageKeys, newPageKeys)) return prev

        if (newPageKeys.size === 0) {
          const nextState = { ...prev }
          delete nextState[pagination.pageIndex]
          return nextState
        }

        return { ...prev, [pagination.pageIndex]: newPagePortion }
      })
    },
    [filteredReferences, pagination.pageIndex]
  )

  // Handler for "Select all" bulk action — selects all rows on the current page
  // and enters "all data selected" mode so the delete path targets every reference
  const handleSelectAllOnPage = useCallback(() => {
    const currentPageSelection: Record<string, boolean> = {}
    filteredReferences.forEach((ref) => {
      currentPageSelection[String(ref.referenceFsiId)] = true
      selectedItemsMapRef.current.set(ref.referenceFsiId, ref)
    })
    setRowSelectionByPages((prev) => ({
      ...prev,
      [pagination.pageIndex]: currentPageSelection
    }))
    setIsAllDataSelected(true)
  }, [filteredReferences, pagination.pageIndex])

  // Handler for "Clear selection" bulk action
  const handleClearSelection = useCallback(() => {
    allRowSelectionRef.current = {}
    setRowSelectionByPages({})
    selectedItemsMapRef.current.clear()
    setIsAllDataSelected(false)
  }, [])

  // Debounced handler for DataGrid's onAllDataSelected — mirrors AddReferencesModal
  const handleOnAllDataSelected = useCallback((isAllSelected: boolean) => {
    if (isAllSelected) {
      allDataSelectedAtRef.current = Date.now()
      setIsAllDataSelected(true)
    } else {
      // Ignore spurious false within 100ms of a true
      if (Date.now() - allDataSelectedAtRef.current < 100) return
      // If selections remain, this false signal is from page navigation (DataGrid
      // internally clears its view on each page change), not from a genuine user clear.
      // allRowSelectionRef.current holds the pre-render value — still non-empty during
      // navigation. Genuine clears go through handleRowSelectionChange or
      // handleClearSelection which reset isAllDataSelected directly.
      if (Object.keys(allRowSelectionRef.current).length > 0) return
      allDataSelectedAtRef.current = 0
      setIsAllDataSelected(false)
    }
  }, [])

  // When isAllDataSelected and the page data reloads (navigation), auto-populate
  // the new page's rows so the DataGrid shows them checked and selectedItemsMapRef
  // accumulates their details for the delete payload.
  useEffect(() => {
    if (!isAllDataSelected || filteredReferences.length === 0) return
    const currentPageSelection: Record<string, boolean> = {}
    filteredReferences.forEach((ref) => {
      currentPageSelection[String(ref.referenceFsiId)] = true
      selectedItemsMapRef.current.set(ref.referenceFsiId, ref)
    })
    setRowSelectionByPages((prev) => {
      const existingKeys = getActiveKeys(prev[pagination.pageIndex])
      const newKeys = getActiveKeys(currentPageSelection)
      if (areSetsEqual(existingKeys, newKeys)) return prev
      return { ...prev, [pagination.pageIndex]: currentPageSelection }
    })
  }, [filteredReferences, isAllDataSelected, pagination.pageIndex])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onClose?.()
  }, [onClose])

  // DataGrid columns
  const gridColumns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: t('DOCUMENT_NAME'),
        id: 'filename',
        textAlign: 'left',
        size: 300,
        enableSorting: true,
        cell: ({ cell, row }: any) => (
          <a
            href={buildUrlWithCSelections(`/show-file/${row.original.referenceFsiId}`)}
            target="_blank"
            rel="noopener noreferrer"
            title={cell.getValue()}
          >
            {cell.getValue()}
          </a>
        )
      }),
      columnHelper.accessor('artifact', {
        header: t('TABLE_TH_ARTIFACT_NO'),
        id: 'artifact',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('product', {
        header: t('EMTF_MD_PRODUCT'),
        id: 'product',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('study', {
        header: t('EMTF_MD_STUDY'),
        id: 'study',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('environment', {
        header: t('EMTF_MD_ENVIRONMENT'),
        id: 'environment',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('country', {
        header: t('EMTF_MD_COUNTRY'),
        id: 'country',
        textAlign: 'left',
        enableSorting: true
      }),
      columnHelper.accessor('site', {
        header: t('EMTF_MD_SITE'),
        id: 'site',
        textAlign: 'left',
        enableSorting: true
      }),
      ...(isReadOnly
        ? []
        : [
            columnHelper.display({
              id: 'actions',
              header: '',
              size: 50,
              cell: ({ row }: any) => (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRemoveReference(row.original.referenceFsiId)}
                  aria-label={t('REMOVE')}
                  className={styles.deleteIconBtn}
                >
                  <Icon>remove_circle</Icon>
                </Button>
              )
            })
          ])
    ],
    [t, isReadOnly, handleRemoveReference]
  )

  const handleGlobalSearch = useCallback((searchValue: string) => {
    setGlobalSearchValue(searchValue)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleClearSearch = useCallback(() => {
    setGlobalSearchValue('')
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handlePaginationChange = useCallback((newPagination: { pageIndex: number; pageSize: number }) => {
    setPagination(newPagination)
  }, [])

  const handleSortingChange = useCallback((newSorting: Array<{ id: string; desc: boolean }>) => {
    if (Object.keys(allRowSelectionRef.current).length > 0) {
      dgInternalClearRef.current = true
    }
    setSorting(newSorting)
  }, [])

  const handleDataGridControlChange = useCallback((state: any) => {
    if (state.pagination) {
      if (Object.keys(allRowSelectionRef.current).length > 0) {
        dgInternalClearRef.current = true
      }
      setPagination(state.pagination)
    }
  }, [])

  if (!fsiId) return null

  return (
    <>
      <Modal show={isOpen} handleClose={handleClose} size="full" className={styles.modalBodyFlex}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <MedidataLogo />
          <Modal.Title className={styles.modalTitleCentered}>{t('BROWSER_REFERENCES_H_TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`theme-sandman ${styles.contentWrapper}`}>
            <Stack direction="horizontal" gap={2} className="mb-3 align-items-center">
              <Icon className={styles.documentIcon}>insert_drive_file</Icon>
              <Typography variant="h3">{fsiName}</Typography>
            </Stack>
            <hr className={styles.headerDivider} />

            {selectAllError && (
              <div className="alert alert-danger mb-3" role="alert">
                {selectAllError}
              </div>
            )}

            {/* Content */}
            {references.length === 0 && !isLoading ? (
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  className={'fs16'}
                  variant="card"
                  defaultImgVariant="noData"
                  title={t('REFERENCES_EMPTY_TITLE', { defaultValue: 'No references to show' })}
                  subtitle={t('REFERENCES_EMPTY_SUBTITLE', {
                    defaultValue: 'You can add references by clicking the button below'
                  })}
                />
                {!isReadOnly && (
                  <Button variant="primary" size="sm" onClick={handleAddReferences} className="mt-3">
                    {t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' })}
                  </Button>
                )}
              </div>
            ) : (
              <>
                {(selectedCount > 0 || isAllDataSelected) && (
                  <div className="onex-data-grid-bulk-actions property-search-bulk-header">
                    <div className="onex-data-grid-bulk-actions__counter counter">
                      <span className="onex-data-grid-bulk-actions__counter_text">
                        {t('PROPERTIES_SEARCH_MULTISELECT_N_SELECTED', { count: selectedDisplayCount })}
                      </span>
                    </div>
                    <div className="onex-data-grid-bulk-actions__actions hasActions">
                      {!isReadOnly && selectedDisplayCount >= 2 && (
                        <Button variant="critical" size="sm" onClick={handleBulkRemoveReferences}>
                          {t('DELETE', { defaultValue: 'Delete' })}
                        </Button>
                      )}
                      <div className="onex-data-grid-bulk-actions__select-all-wrapper">
                        {!isAllDataSelected && totalElements > 0 && (
                          <Button variant="primary" size="sm" buttonStyle="ghost" onClick={handleSelectAllOnPage}>
                            {t('PROPERTIESSEARCH_MULTISELECT_SELECT_ALL_IN_SECTION', { allItems: totalElements })}
                          </Button>
                        )}
                        <Button variant="secondary" buttonStyle="ghost" size="sm" onClick={handleClearSelection}>
                          {t('CLEAR_SELECTED')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <DataGrid
                  key={fsiId} // Force remount when fsiId changes to reset internal state
                  isLoading={isLoading}
                  dataTestId="document-references-datagrid"
                  columns={gridColumns}
                  data={filteredReferences}
                  getRowId={(row: EnrichedReference) => String(row.referenceFsiId)}
                  enablePagination={true}
                  manualPagination={true}
                  pagination={pagination}
                  onPaginationChange={handlePaginationChange}
                  pageCount={Math.max(1, Math.ceil(totalElements / pagination.pageSize))}
                  onChangeControl={handleDataGridControlChange}
                  manualDataLength={totalElements}
                  rowsPerPageOptions={PAGE_SIZES_ARRAY}
                  title={t('BROWSER_REFERENCES_H_TITLE')}
                  enableGlobalFilter={true}
                  onGlobalSearchChange={handleGlobalSearch}
                  onClearSearch={handleClearSearch}
                  showClearBtnInSearch={!!globalSearchValue}
                  enableRowSelection={!isReadOnly}
                  onRowSelectionChange={handleRowSelectionChange}
                  enableSorting={true}
                  manualSorting={true}
                  onSortingChange={handleSortingChange}
                  emptyStateOptions={{ defaultImgVariant: 'noData' }}
                  isCursorPagination={true}
                  dataGridControlProps={{
                    primaryActionText: t('REFERENCES_BTN_ADD', { defaultValue: 'Add references' }),
                    primaryAction: handleAddReferences,
                    hasActionBtn: !isReadOnly,
                    hasViewOptions: false,
                    hasSecondaryActions: false,
                    searchFieldProps: {
                      maxLength: 255
                    }
                  }}
                  stickySelection={true}
                  className="document-references-datagrid"
                  state={{
                    pagination,
                    sorting,
                    rowSelection: allRowSelectionObj
                  }}
                />
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        title="Delete"
        show={showConfirmationDialog}
        backdrop={false}
        handleClose={handleCancel}
        isConfirmationDialog
      >
        <Modal.Body>
          {isBulkDeletePending ? (
            isAllDataSelected ? (
              `Are you sure you want to delete all ${selectedDisplayCount} references?`
            ) : (
              'Are you sure you want to delete all the selected references?'
            )
          ) : (
            <>
              Are you sure you want to delete following references?
              <br />
              <strong>{references.find((r) => r.referenceFsiId === pendingDeleteId)?.filename}</strong>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleLeavePage}>
            Yes
          </Button>
          <Button onClick={handleCancel} variant="secondary">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default DocumentReferencesModal
